import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Table,
  Button,
  Modal,
  Form,
  DatePicker,
  TimePicker,
  Select,
  Checkbox,
  message,
  Progress,
  Popconfirm,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { supabase } from "../supabase";

const Schedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [form] = Form.useForm();
  const [students, setStudents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [progressData, setProgressData] = useState({});

  useEffect(() => {
    fetchSchedules();
    fetchStudents();
    fetchLessons();
  }, []);

  const fetchProgressForStudent = async (studentId, lessonId) => {
    try {
      const { data, error } = await supabase
        .from("student_lesson_progress")
        .select("*")
        .eq("student_id", studentId)
        .eq("lesson_id", lessonId)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data;
    } catch (error) {
      console.error("Error fetching progress:", error);
      return null;
    }
  };

  const fetchStudents = async () => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select("id, name");
      if (error) throw error;
      setStudents(data);
    } catch (error) {
      message.error("Error fetching students");
    }
  };

  const fetchLessons = async () => {
    try {
      const { data, error } = await supabase
        .from("lessons")
        .select("id, title");
      if (error) throw error;
      setLessons(data);
    } catch (error) {
      message.error("Error fetching lessons");
    }
  };

  const fetchSchedules = async () => {
    try {
      const { data, error } = await supabase.from("lesson_schedules").select(`
          *,
          students (name),
          lessons (
            title,
            lesson_contents (
              id,
              title,
              content_type,
              order,
              content_data
            )
          )
        `);
      if (error) throw error;

      // Fetch progress for each schedule
      const progressPromises = data.map(async (schedule) => {
        const progress = await fetchProgressForStudent(
          schedule.student_id,
          schedule.lesson_id
        );
        return {
          key: `${schedule.student_id}-${schedule.lesson_id}`,
          progress,
        };
      });

      const progressResults = await Promise.all(progressPromises);
      const progressMap = progressResults.reduce((acc, curr) => {
        acc[curr.key] = curr.progress;
        return acc;
      }, {});

      setProgressData(progressMap);
      setSchedules(data);
    } catch (error) {
      message.error("Error fetching schedules");
    } finally {
      setLoading(false);
    }
  };

  const showModal = (schedule = null) => {
    setEditingSchedule(schedule);
    setIsModalVisible(true);
    if (schedule) {
      // Convert the ISO string date to a moment object
      const formattedSchedule = {
        ...schedule,
        scheduled_date: schedule.scheduled_date
          ? moment(schedule.scheduled_date)
          : null,
      };
      form.setFieldsValue(formattedSchedule);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const deleteSchedule = async (scheduleId) => {
    try {
      const { error } = await supabase
        .from("lesson_schedules")
        .delete()
        .eq("id", scheduleId);
      if (error) throw error;
      message.success("Schedule deleted successfully");
      fetchSchedules();
    } catch (error) {
      message.error("Error deleting schedule");
    }
  };

  const handleSubmit = async (values) => {
    try {
      // Convert the moment object to ISO string for the database
      const formattedValues = {
        ...values,
        scheduled_date: values.scheduled_date?.toISOString(),
      };

      if (editingSchedule) {
        const { error } = await supabase
          .from("lesson_schedules")
          .update(formattedValues)
          .eq("id", editingSchedule.id);
        if (error) throw error;
        message.success("Schedule updated successfully");
      } else {
        const { error } = await supabase
          .from("lesson_schedules")
          .insert([formattedValues]);
        if (error) throw error;
        message.success("Schedule added successfully");
      }
      fetchSchedules();
      handleCancel();
    } catch (error) {
      message.error("Error saving schedule");
    }
  };

  const columns = [
    {
      title: "Student Name",
      dataIndex: ["students", "name"],
      key: "student_name",
      width: "20%",
    },
    {
      title: "Recurring",
      dataIndex: "recurring",
      key: "recurring",
      render: (recurring) => (recurring ? "Yes" : "No"),
    },
    {
      title: "Recurrence Pattern",
      dataIndex: "recurrence_pattern",
      key: "recurrence_pattern",
    },
    {
      title: "Lesson Name",
      dataIndex: ["lessons", "title"],
      key: "lesson_name",
    },
    {
      title: "Scheduled Date",
      dataIndex: "scheduled_date",
      key: "scheduled_date",
    },
    {
      title: "Progress",
      key: "progress",
      width: "15%",
      render: (text, record) => {
        const progress =
          progressData[`${record.student_id}-${record.lesson_id}`];
        if (!progress?.data) return "Not started";

        const lessonContents = (record.lessons.lesson_contents || []).sort(
          (a, b) => a.order - b.order
        );
        let countSlides = 0;
        for (const section of lessonContents) {
          let contentData = JSON.parse(section.content_data);
          countSlides += contentData.questions.length + 1;
          countSlides +=
            (contentData.activities && contentData.activities.length) || 0;
        }

        const totalSections = lessonContents.length;

        if (totalSections === 0) return "No content";

        console.log("countSlides", countSlides);

        // Add 1 for intro slide, multiply sections by 3 for content+questions
        const totalSlides = countSlides;
        const currentSlide = progress.data.slideIndex || 0;
        console.log("lessonContents", lessonContents);
        const overallProgress = Math.round(
          (currentSlide / (totalSlides - 1)) * 100
        );

        return (
          <Progress
            type="circle"
            percent={overallProgress}
            width={40}
            status={overallProgress === 100 ? "success" : "active"}
          />
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button onClick={() => showModal(record)}>Edit</Button>
          <Popconfirm
            title="Are you sure you want to delete this schedule?"
            onConfirm={() => deleteSchedule(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>Delete</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        Add Schedule
      </Button>
      <Table
        columns={columns}
        dataSource={schedules}
        loading={loading}
        rowKey="id"
        expandable={{
          expandedRowRender: (record) => {
            const progress =
              progressData[`${record.student_id}-${record.lesson_id}`];
            if (!progress?.data) return <p>No progress data available</p>;

            const lessonContents = (record.lessons.lesson_contents || []).sort(
              (a, b) => a.order - b.order
            );
            if (lessonContents.length === 0)
              return <p>No lesson content available</p>;

            return (
              <div
                style={{
                  padding: "20px",
                  background: "#fafafa",
                  borderRadius: "8px",
                }}
              >
                <div style={{ marginBottom: "16px" }}>
                  <h4>Detailed Progress</h4>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(250px, 1fr))",
                    gap: "16px",
                  }}
                >
                  {lessonContents.map((content, index) => {
                    // Calculate section progress based on content structure
                    const lessonContentData = JSON.parse(content.content_data);
                    const currentSlide = progress.data.slideIndex || 0;

                    // Calculate total slides for previous sections
                    let previousSlidesCount = 1; // Start with 1 for intro slide
                    for (let i = 0; i < index; i++) {
                      const prevContent = JSON.parse(
                        lessonContents[i].content_data
                      );
                      previousSlidesCount += 2 + prevContent.questions.length; // Content slide + questions
                    }

                    // Calculate slides for current section
                    const sectionStartSlide = previousSlidesCount;
                    const totalSectionSlides =
                      1 + lessonContentData.questions.length; // Content slide + questions
                    const sectionEndSlide =
                      sectionStartSlide + totalSectionSlides - 1;

                    let sectionProgress = 0;
                    if (currentSlide > sectionEndSlide) {
                      // If we're past this section's end, it's complete
                      sectionProgress = 100;
                    } else if (currentSlide >= sectionStartSlide) {
                      // If we're in this section, calculate progress
                      const slidesCompleted =
                        currentSlide - sectionStartSlide + 1;
                      sectionProgress = Math.round(
                        (slidesCompleted / totalSectionSlides) * 100
                      );
                    }

                    return (
                      <div
                        key={content.id}
                        style={{
                          background: "#fff",
                          padding: "16px",
                          borderRadius: "6px",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 500, marginBottom: "8px" }}>
                            {content.title}
                          </div>
                          <div style={{ color: "#666", fontSize: "12px" }}>
                            Section {index + 1}
                          </div>
                        </div>
                        <Progress
                          type="circle"
                          percent={sectionProgress}
                          width={50}
                          status={
                            sectionProgress === 100 ? "success" : "active"
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          },
        }}
      />
      <Modal
        title={editingSchedule ? "Edit Schedule" : "Add Schedule"}
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={form.submit}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="scheduled_date"
            label="Scheduled Date"
            rules={[
              { required: true, message: "Please select the scheduled date!" },
            ]}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            name="student_id"
            label="Student ID"
            rules={[{ required: true, message: "Please select the student!" }]}
          >
            <Select>
              {/* Options should be populated dynamically */}
              {students.map((student) => (
                <Select.Option key={student.id} value={student.id}>
                  {student.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="lesson_id"
            label="Lesson ID"
            rules={[{ required: true, message: "Please select the lesson!" }]}
          >
            <Select>
              {/* Options should be populated dynamically */}
              {lessons.map((lesson) => (
                <Select.Option key={lesson.id} value={lesson.id}>
                  {lesson.title}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="recurring" valuePropName="checked">
            <Checkbox>Recurring</Checkbox>
          </Form.Item>
          <Form.Item
            name="recurrence_pattern"
            label="Recurrence Pattern"
            rules={[{ required: false }]}
          >
            <Select>
              <Select.Option value="daily">Daily</Select.Option>
              <Select.Option value="weekly">Weekly</Select.Option>
              <Select.Option value="monthly">Monthly</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Schedule;
